import { registerInterceptor, FetcherInterceptor } from '@goldcast/api/backend';
import { registerInterceptor as registerVideoInterceptor } from '@goldcast/api/content';
import logger from '@goldcast/logger';

import router from '@/router';
import store from '@/store';
import { getEnv } from '@/utils/env';

const backendUrl = getEnv('VUE_APP_API_BACKEND') ?? '';
const contentBackendUrl = getEnv('VUE_APP_CL_BACKEND_URL') ?? '';

const APP_AREA = 'admin';

type Register = typeof registerInterceptor;

/** Strip the query string so sensitive params (tokens, ids) are never logged. */
function safePath(url: string | undefined): string {
  if (!url) {
    return '';
  }
  return url.split('?')[0];
}

/** Best-effort diagnostic — wrapped so a logging failure never changes control flow. */
function logApiFailure(params: {
  method?: string;
  path: string;
  status?: number;
  failureCategory: string;
  retryContext?: string;
}): void {
  try {
    logger.info('api_failure', {
      appArea: APP_AREA,
      method: params.method ?? 'GET',
      path: params.path,
      ...(params.status !== undefined && { status: params.status }),
      failureCategory: params.failureCategory,
      ...(params.retryContext && { retryContext: params.retryContext })
    });
  } catch {
    // Diagnostics must never affect API behavior.
  }
}

function initializeFetchRegister(url: string, register: Register): void {
  try {
    register(FetcherInterceptor.PRE_PROCESS_OPTIONS, ({ options }) => {
      const jwtToken =
        store.state.user?.userCognitoDetails?.signInUserSession?.accessToken?.jwtToken ??
        store.state.user?.userCognitoDetails?.signInUserSession?.idToken?.jwtToken;

      options.headers = {
        ...options.headers,
        ...(!!jwtToken && { Authorization: `Bearer ${jwtToken}` })
      };
      options.url = options.url.startsWith(url || '') ? options.url : `${url}${options.url}`;
      return options;
    });
    register(FetcherInterceptor.ON_ERROR, ({ error, options }) => {
      logger.error('[fetchRegister] fetch interceptor error', error as Error);
      logApiFailure({
        method: options?.method,
        path: safePath(options?.url),
        failureCategory: 'network_error'
      });
    });
    register(FetcherInterceptor.POST_PROCESS_RESPONSE, async ({ response, options, retryFn }) => {
      if (response?.status && response?.status >= 500) {
        let error: null | Error = null;
        try {
          error = new Error(JSON.stringify(await response.json()));
        } catch {
          error = new Error('Internal server error');
        }
        error.name = '';
        (error as any).statusCode = response.status;
        logApiFailure({
          method: options?.method,
          path: safePath(options?.url),
          status: response.status,
          failureCategory: 'server_error',
          retryContext: options?.unAuthTries ? 'auth_retry' : undefined
        });
        throw error;
      } else if (response && response.status !== 204) {
        if (response.status === 401 && !options?.unAuthTries) {
          await store.dispatch('user/getNewAccessToken', null, { root: true });
          options.unAuthTries = 1;
          logApiFailure({
            method: options?.method,
            path: safePath(options?.url),
            status: response.status,
            failureCategory: 'auth_error',
            retryContext: 'auth_retry'
          });
          return retryFn?.(options);
        }
        if (response.status === 401 && options.unAuthTries && options.unAuthTries >= 1) {
          const error = new Error(JSON.stringify(await response.json()));
          error.name = '';
          (error as any).statusCode = response.status;
          logApiFailure({
            method: options?.method,
            path: safePath(options?.url),
            status: response.status,
            failureCategory: 'auth_error',
            retryContext: 'auth_retry_exhausted'
          });
          store.dispatch('user/signUserOut', null, { root: true }).then(() => {
            router.push({ name: 'Login' });
          });
          throw error;
        } else if (response.status >= 400) {
          const data = await response.json();
          const error = new Error(data?.message ?? data?.detail ?? JSON.stringify(data));
          error.name = '';
          (error as any).statusCode = response.status;
          if (data && data['code']) {
            (error as any).apiErrorCode = data['code'];
          }
          if (data && data['detail']) {
            (error as any).detail = data['detail'];
          }
          logApiFailure({
            method: options?.method,
            path: safePath(options?.url),
            status: response.status,
            failureCategory: 'client_error',
            retryContext: options?.unAuthTries ? 'auth_retry' : undefined
          });
          throw error;
        } else {
          return response;
        }
      }
    });
  } catch (error) {
    logger.error('[fetchRegister] response interceptor failed', error);
  }
}

export default function initializeFetchers(): void {
  try {
    initializeFetchRegister(backendUrl, registerInterceptor);
    initializeFetchRegister(contentBackendUrl, registerVideoInterceptor);
  } catch (error: any) {
    logger.error('[fetchRegister] initialization failed', error);
  }
}
